import { makeObservable, computed } from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SpaceAccountCalendarModelData } from "@/store/calendar/space-account-calendar/types";
import { SpaceAccountCalendarObservable } from "@/store/calendar/space-account-calendar/SpaceAccountCalendarObservable";
import { SyncUpdateValue } from "@/store/sync/types";

export class AppStoreSpaceAccountCalendarStore extends BaseSyncModelStore<
  SpaceAccountCalendarObservable,
  SpaceAccountCalendarModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountCalendar, ...injectedDeps });
    makeObservable(this, {
      createSyncModel: false,
      failedOrRetryingCalendars: computed,
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountCalendarModelData>
  ): SpaceAccountCalendarObservable {
    return new SpaceAccountCalendarObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  /**
   * Returns all calendars with FAILED or TRYING_TO_CONNECT status,
   * ordered by worst status first (FAILED, then TRYING_TO_CONNECT), then by name.
   */
  get failedOrRetryingCalendars(): SpaceAccountCalendarObservable[] {
    // We always need to have all calendars in memory to do this
    const allCalendars = this.all;
    const failedOrTrying = allCalendars.filter(calendar => {
      const status = calendar.calendarSyncStatus;
      return status === "FAILED" || status === "TRYING_TO_CONNECT";
    });

    return failedOrTrying.sort((a, b) => {
      const statusA = a.calendarSyncStatus;
      const statusB = b.calendarSyncStatus;

      // Sort by status: FAILED comes before TRYING_TO_CONNECT
      if (statusA === "FAILED" && statusB === "TRYING_TO_CONNECT") {
        return -1;
      }
      if (statusA === "TRYING_TO_CONNECT" && statusB === "FAILED") {
        return 1;
      }

      // If same status (or both null somehow), sort by name
      const nameA = a.primaryLabel ?? "";
      const nameB = b.primaryLabel ?? "";
      return nameA.localeCompare(nameB);
    });
  }
}
